/**
 * The household as a group of people.
 *
 * THE MODEL THIS SCREEN MAKES VISIBLE: every account has a household of its
 * own, made when it signed up. Sharing is something you do on top of that —
 * you invite people into yours, or make another household and invite them
 * there. So removing someone is not a punishment or a lockout: they go back to
 * their own household, with their own entries, untouched. The screen says that
 * in the confirm line, because "Remove" on its own reads like deletion and
 * people hesitate over it for the wrong reason.
 *
 * WHY THE TOKEN IS SHOWN ONCE. Only its hash is stored, so there is no "show
 * me that link again" to build — the point of hashing it is that the server
 * cannot produce it a second time. The UI therefore has to make the one
 * showing count: the link appears in a box, selected on focus, with a copy
 * button, and it stays until dismissed rather than vanishing on the next
 * render.
 */
import { useEffect, useState } from 'react';
import type { Household as HouseholdType } from '@et/contracts';
import { api, authApi, messageOf, saveIdentity, type Identity } from './api.js';

type Role = 'OWNER' | 'ADULT' | 'VIEWER';

const ROLE_BLURB: Record<Role, string> = {
  OWNER: 'Everything, including who is in the household',
  ADULT: 'Record and edit spending',
  VIEWER: 'Read only',
};

interface Invite {
  id: string;
  role: string;
  expiresAt: string;
  createdAt: string;
}

export function HouseholdSettings({
  identity,
  household,
  canEdit,
  onChanged,
}: {
  identity: Identity;
  household: HouseholdType;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [name, setName] = useState(household.name);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [fresh, setFresh] = useState<{ token: string; role: string } | null>(null);
  const [role, setRole] = useState<Role>('ADULT');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  /** The "start another one" form, collapsed until asked for. */
  const [newName, setNewName] = useState('');
  const [newCurrency, setNewCurrency] = useState('EUR');
  const [adding, setAdding] = useState(false);

  useEffect(() => setName(household.name), [household.name]);

  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    api
      .invites(identity)
      .then((r) => {
        if (!cancelled) setInvites(r.invites);
      })
      .catch(() => {
        /* a missing invite list is not worth an error banner over the screen */
      });
    return () => {
      cancelled = true;
    };
  }, [identity, canEdit]);

  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  async function refreshInvites() {
    setInvites((await api.invites(identity)).invites);
  }

  const live = household.members;
  const meIsOwner = canEdit;
  /** The link the invited person opens. Same origin, so it just works. */
  const linkFor = (token: string) => `${window.location.origin}/join/${token}`;

  return (
    <section className="settings">
      <div className="settings-block">
        <h2>Name</h2>

        {error && <p className="error">{error}</p>}

        <label className="signin-field" style={{ maxWidth: 340 }}>
          <span className="field-label">Name</span>
          <input
            value={name}
            disabled={!canEdit || busy}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              const next = name.trim();
              if (!next || next === household.name) {
                setName(household.name);
                return;
              }
              void run(async () => {
                await api.renameHousehold(identity, next);
                onChanged();
              });
            }}
            aria-label="Household name"
          />
        </label>
      </div>

      <div className="settings-block">
        <h2>People</h2>
        {!canEdit && <p className="hint">Only an owner can change who is in the household.</p>}

        <div className="cat-group">
          {live.map((m) => {
            const isMe = m.id === household.meMemberId;
            return (
              <div className="cat-row" key={m.id}>
                <span className="cat-name">
                  {m.displayName}
                  {isMe && <span className="cat-uses">you</span>}
                </span>

                {canEdit ? (
                  <select
                    value={m.role}
                    disabled={busy}
                    aria-label={`Role for ${m.displayName}`}
                    onChange={(e) =>
                      void run(async () => {
                        await api.updateMember(identity, m.id, { role: e.target.value });
                        onChanged();
                      })
                    }
                  >
                    {(Object.keys(ROLE_BLURB) as Role[]).map((r) => (
                      <option key={r} value={r}>
                        {r.charAt(0) + r.slice(1).toLowerCase()} — {ROLE_BLURB[r]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="hint">{ROLE_BLURB[m.role as Role]}</span>
                )}

                {/* The sentence matters more than the button. "Remove" alone
                    reads as deletion, and people hesitate over it for the wrong
                    reason — nothing of theirs is lost. */}
                {canEdit && !isMe && (
                  <button
                    type="button"
                    className="chip chip-ghost cat-archive"
                    disabled={busy}
                    onClick={() => {
                      if (
                        !window.confirm(
                          `Remove ${m.displayName} from ${household.name}?\n\n` +
                            'Their account and their own household are untouched — they simply ' +
                            'stop seeing this one. Everything they recorded here stays in the ' +
                            'journal, still in their name.',
                        )
                      )
                        return;
                      void run(async () => {
                        await api.removeMember(identity, m.id);
                        onChanged();
                      });
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {meIsOwner && (
        <div className="settings-block">
          <h2>Invite someone</h2>
          <p className="hint">
            Make a link and send it however you normally talk to them. Whoever opens it joins this
            household — so send it to one person, not a group. It works once and expires in a week.
          </p>

          {fresh && (
            <div className="claim-offer">
              <p>
                <strong>Copy this now.</strong> Only a fingerprint of it is stored, so it cannot be
                shown again — if you lose it, revoke it below and make another.
              </p>
              <input
                className="invite-link"
                readOnly
                value={linkFor(fresh.token)}
                onFocus={(e) => e.target.select()}
                aria-label="Invite link"
              />
              <div className="currency-confirm-actions">
                <button
                  type="button"
                  className="primary compact"
                  onClick={() => {
                    void navigator.clipboard?.writeText(linkFor(fresh.token));
                    setCopied(true);
                  }}
                >
                  {copied ? 'Copied' : 'Copy link'}
                </button>
                <button
                  type="button"
                  className="chip"
                  onClick={() => {
                    setFresh(null);
                    setCopied(false);
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}

          <div className="cat-row cat-add">
            <select value={role} onChange={(e) => setRole(e.target.value as Role)} aria-label="Role for the invited person">
              {(['ADULT', 'VIEWER'] as Role[]).map((r) => (
                <option key={r} value={r}>
                  {r.charAt(0) + r.slice(1).toLowerCase()} — {ROLE_BLURB[r]}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="chip chip-on"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const made = await api.createInvite(identity, { role });
                  setFresh({ token: made.token, role: made.role });
                  setCopied(false);
                  await refreshInvites();
                })
              }
            >
              Make a link
            </button>
          </div>
          {/* Ownership is not offered here on purpose — see the service. */}
          <p className="hint">
            A link cannot make someone an owner. Invite them, then change their role above once you
            can see they have joined.
          </p>

          {invites.length > 0 && (
            <>
              <h3 className="field-label">Links waiting to be used</h3>
              <div className="cat-group">
                {invites.map((i) => (
                  <div className="cat-row" key={i.id}>
                    <span className="cat-name">
                      {i.role.charAt(0) + i.role.slice(1).toLowerCase()}
                      <span className="cat-uses">expires {i.expiresAt.slice(0, 10)}</span>
                    </span>
                    <button
                      type="button"
                      className="chip chip-ghost cat-archive"
                      disabled={busy}
                      onClick={() =>
                        void run(async () => {
                          await api.revokeInvite(identity, i.id);
                          await refreshInvites();
                        })
                      }
                    >
                      Revoke
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* A SECOND HOUSEHOLD, kept at the bottom and behind a link on purpose.
          For a family tracker this is a rare, deliberate act — a shared flat, a
          parent's finances — and putting it in the nav invites somebody to make
          one by accident and then wonder where their entries went. It is not
          hidden, just not offered. */}
      <div className="settings-block">
        <h2>Another household</h2>
        <p className="hint">
          Keep a second set of books — a shared flat, a parent&rsquo;s finances — separate from
          this one. Nothing is shared between households: its own categories, its own budget, its
          own people. You can move between them from the picker at the bottom of the sidebar.
        </p>

        {!adding ? (
          <p className="signin-switch" style={{ textAlign: 'left' }}>
            <button type="button" className="linkish" onClick={() => setAdding(true)}>
              Start another household
            </button>
          </p>
        ) : (
          <div className="cat-row cat-add">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Flat on Hauptstraße"
              aria-label="New household name"
              autoFocus
            />
            <input
              className="currency-mini"
              value={newCurrency}
              onChange={(e) => setNewCurrency(e.target.value.toUpperCase().slice(0, 3))}
              maxLength={3}
              aria-label="Currency for the new household"
            />
            <button
              type="button"
              className="chip chip-on"
              disabled={busy || !newName.trim() || newCurrency.length !== 3}
              onClick={() =>
                void run(async () => {
                  const made = await authApi.newHousehold({
                    name: newName.trim(),
                    currency: newCurrency,
                  });
                  if (!made.ok) {
                    setError(made.message ?? 'Could not create that household.');
                    return;
                  }
                  // Switch straight into it — making one and then having to go
                  // find it is a step nobody wants.
                  if (made.householdId) {
                    saveIdentity({ householdId: made.householdId, memberId: '' });
                    window.location.assign('/');
                  }
                })
              }
            >
              Create
            </button>
            <button type="button" className="chip" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
